import { RTKQueryTag } from 'src/common/constants';
import type { ApiResponse } from 'src/common/interfaces';
import type { NetworkInstance } from 'src/common/interfaces/networks/instances';
import { apiSlice } from 'src/features/api/api-slice';

export const networkInstancesSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    deployNetworkInstance: builder.mutation<
      NetworkInstance | undefined,
      { templateId: string; roomId?: string; privateNetworkInstance?: boolean }
    >({
      query: ({ templateId, roomId, privateNetworkInstance }) => ({
        url: '/networks/instances/',
        method: 'POST',
        body: {
          templateId,
          roomId,
          privateNetworkInstance,
        },
      }),
      transformResponse: (response: ApiResponse<NetworkInstance>) => response.data,
      invalidatesTags: [RTKQueryTag.Networks],
    }),
    getNetworkInstances: builder.query<
      NetworkInstance[],
      { templateId?: string; showOne?: boolean; status?: string[]; private?: boolean; userId?: string }
    >({
      query: (params) => ({
        url: `/networks/instances/`,
        method: 'GET',
        params: {
          templateId: params.templateId,
          status: params.status?.join(','),
          private: params.private,
          userId: params.userId,
        },
      }),
      transformResponse: (response: ApiResponse<NetworkInstance[]>) => response.data || [],
      providesTags: [RTKQueryTag.Networks],
    }),
    getCurrentActiveNetwork: builder.query<
      NetworkInstance | undefined,
      { userId?: string; templateId?: string; showOne?: boolean }
    >({
      query: (params) => ({
        url: `/networks/instances/`,
        method: 'GET',
        params: {
          templateId: params.templateId,
          userId: params.userId,
        },
      }),
      transformResponse: (response: ApiResponse<NetworkInstance[]>) => response.data?.[0],
      providesTags: (result) => [{ type: RTKQueryTag.Networks, id: result?._id }],
    }),
    getNetworkInstance: builder.query<NetworkInstance | undefined, { networkInstanceId?: string }>({
      query: ({ networkInstanceId }) => ({
        url: `/networks/instances/${networkInstanceId ?? ''}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<NetworkInstance>) => response.data,
      providesTags: (result) => [{ type: RTKQueryTag.Networks, id: result?._id }],
    }),
    updateNetwork: builder.mutation<
      ApiResponse<NetworkInstance>,
      {
        networkInstanceId: string;
        pauseTime?: Date;
        redeploy?: boolean;
        reset?: boolean;
        forceReset?: boolean;
        unpause?: boolean;
      }
    >({
      query: ({ networkInstanceId, pauseTime, redeploy, reset, forceReset, unpause }) => ({
        url: `networks/instances/${networkInstanceId}`,
        method: 'PUT',
        body: { pauseTime, redeploy, reset, forceReset, unpause },
      }),
      invalidatesTags: (result, error) => (!error && result ? [{ type: RTKQueryTag.Networks }] : []),
    }),
    addUserToNetwork: builder.mutation<ApiResponse<NetworkInstance>, { networkInstanceId: string; userId: string }>({
      query: ({ networkInstanceId, userId }) => ({
        url: `networks/instances/${networkInstanceId}/users`,
        method: 'POST',
        body: { userId },
      }),
      invalidatesTags: (result, error) => (!error && result ? [{ type: RTKQueryTag.Networks }] : []),
    }),
    removeUserFromNetwork: builder.mutation<
      ApiResponse<NetworkInstance>,
      { networkInstanceId: string; userId: string }
    >({
      query: ({ networkInstanceId, userId }) => ({
        url: `networks/instances/${networkInstanceId}/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error) => (!error && result ? [{ type: RTKQueryTag.Networks }] : []),
    }),
    destroyNetwork: builder.mutation<ApiResponse<NetworkInstance>, { networkInstanceId: string }>({
      query: ({ networkInstanceId }) => ({
        url: `networks/instances/${networkInstanceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error) => (!error && result ? [{ type: RTKQueryTag.Networks }] : []),
    }),
  }),
});

export const {
  useAddUserToNetworkMutation,
  useDestroyNetworkMutation,
  useGetNetworkInstancesQuery,
  useGetNetworkInstanceQuery,
  useDeployNetworkInstanceMutation,
  useGetCurrentActiveNetworkQuery,
  useRemoveUserFromNetworkMutation,
  useUpdateNetworkMutation,
} = networkInstancesSlice;
